import * as React from 'react';
import translate from '../../../../locale/translate';

import { getDisplayValue, formatDate } from '../../util/StringUtil';
import ISettings from './ISettings';

import { SU } from '../../../../interfaces';
import objectAssign = require('object-assign');

interface IProps {
    title: string,
    jobs: SU.IJob[],
    settings: ISettings,
}

const AVAILABLE_COLS = [
    'State',
    'Type',
    'Message',
    'StartTime',
    'EndTime',
];

const COL_TITLES = {
    State: translate.gettext("State"),
    Type: translate.gettext("Type"),
    Message: translate.gettext("Message"),
    StartTime: translate.gettext("Start time"),
    EndTime: translate.gettext("End time"),
};

const HEADER_CLASSES = {
    State: 'small col-xs-1',
    Type: 'small col-xs-3',
    Message: 'small col-xs-4',
    StartTime: 'small col-xs-2',
    EndTime: 'small col-xs-2',
};

const COL_CLASSES = {
    State: 'small',
    Type: 'small',
    Message: 'small',
    StartTime: 'small',
    EndTime: 'small',
};

const STATE_CLASSES = {
    'CREATED': 'fa-circle-thin',
    'QUEUED': 'fa-clock-o',
    'RUNNING': 'fa-cog fa-spin',
    'SUCCESSFUL': 'fa-check text-success',
    'FAILED': 'fa-exclamation-triangle text-danger',
    'CANCELLED': 'fa-times text-warning',
    'DELETED': 'fa-times text-warning'
};

const JOB_CODES = {
    1: translate.gettext('Missing mandatory parameter; see logfile for further information'),
    2: translate.gettext('Missing input file for Install or Scan command'),
    3: translate.gettext('bRCT is not startet with administrative permissions'),
    4: translate.gettext('Unexpected error, see result file and logfile for further information'),
    5: translate.gettext('One or more patches failed during installation'),
    6: translate.gettext('Another running instance of bRCT has been detected'),
    7: translate.gettext('Failed to initialize extended logging; see logfile for further information'),
    8: translate.gettext('Install succeeded, but a reboot will be required'),
    9: translate.gettext('An Error Occured, and a reboot will be required'),
    10: translate.gettext('Execution not started because a pending reboot was detected'),
    11: translate.gettext('The wsusscn2.cab is corrupted and cannot be used for the software scan')
};

export default class JobTable extends React.Component<IProps, {}> {
    static propTypes = {
        title: React.PropTypes.string.isRequired,
        jobs: React.PropTypes.array,
        settings: React.PropTypes.object.isRequired,
    };

    assignLastLog(job: SU.IJob) {
        let jobWithAdditionalData = objectAssign({}, job);

        if (job.resultMeta && job.resultMeta.type) {
            let log = JSON.parse(JSON.stringify(job.log));
            log = log.reverse();
            let logItem = log.find(l => {
                return l.type == job.resultMeta.type && job.success === false;
            });

            if (logItem) {
                jobWithAdditionalData.lastLog = logItem;
            }
        }
        if (!jobWithAdditionalData.lastLog) {
            jobWithAdditionalData.lastLog = job.log[(job.log.length - 1)];
        }

        return jobWithAdditionalData;
    }

    applyTypeAndStateFilter(job: SU.IJob, settings: ISettings) {
        if (!settings.displayDownloadJobs && job.jobType == "DOWNLOAD") {
            return false;
        }
        if (!settings.displayInstallJobs && job.jobType == "INSTALL") {
            return false;
        }
        if (!settings.displayScanJobs && job.jobType == "SCAN") {
            return false;
        }

        if (!settings.displaySuccessfulJobs && job.success) {
            return false;
        }

        if (!settings.displayFailedJobs && (job.resultOn && !job.success)) {
            return false;
        }

        if (!settings.displayInProgressJobs && !job.resultOn) {
            return false;
        }

        if (!settings.displaySuccessfulJobs && !settings.displayInProgressJobs && job.resultMeta && !job.resultMeta.success && job.resultMeta.type.indexOf('REQUIREMENT') > -1) {
            return false;
        }

        return true;
    }


    renderColumnHead(name: string, settings: ISettings) {
        if (getDisplayValue(name, settings)) {
            return (
                <th key={name} className={HEADER_CLASSES[name]}>{COL_TITLES[name]}</th>
            );
        }

        return;
    }

    renderState(job: SU.IJob) {
        let state;
        if (job.resultOn && !job.success) {
            if (job.resultMeta && !job.resultMeta.success && job.resultMeta.type.indexOf('REQUIREMENT') > -1) {
                state = translate.gettext(`Info`);
            } else {
                state = translate.gettext("Failed");
            }
        } else if (job.success) {
            state = translate.gettext("OK");
        }
        return state;
    }

    renderMessage(message: string, job: SU.IJob) {
        let retVal = [];

        retVal.push(<p key="1">{job.lastLog.message}</p>);

        if (job.lastLog && job.lastLog.meta && job.lastLog.meta.exitCode) {
            retVal.push(<p key="2">Code: {job.lastLog.meta.exitCode}</p>)
        }

        if (job.lastLog && job.lastLog.meta && job.lastLog.meta.event) {
            retVal.push(<p key="3">{job.lastLog.meta.event}</p>)
        }

        return retVal;
    }

    renderColumn(name: string, job: SU.IJob, settings: ISettings) {
        if (getDisplayValue(name, settings)) {
            let value = job[name];

            if (name === 'StartTime' || name === 'EndTime') {
                if (name === 'StartTime' && !job.createdOn) {
                    value = translate.gettext('Job not started');
                } else if (name === 'EndTime' && !job.resultOn) {
                    value = translate.gettext('Job not finished');
                } else {
                    value = (name === 'StartTime') ? formatDate(job.createdOn, 'L LT') : formatDate(job.resultOn, 'L LT');
                }
            }

            if (name === 'State') {
                value = this.renderState(job);
            }

            if (name === 'Message') {
                value = this.renderMessage(value, job);
            }

            if (name === "Type") {
                let type = job.jobType;
                if (job.patchName) {
                    type = `${type} ${job.patchName}`;
                }

                value = type;
            }

            return (
                <td key={name} className={COL_CLASSES[name]}>{value}</td>
            );
        }

        return;
    }

    renderRow(job: SU.IJob, idx: number, settings: ISettings) {
        return (
            <tr key={job.jobId}>
                {AVAILABLE_COLS.map(name => {
                    return this.renderColumn(name, job, settings);
                })}
            </tr>
        );
    }

    render() {
        let { jobs } = this.props;
        const { title, settings } = this.props;

        const key = `jobs-${title}`;
        const headers = AVAILABLE_COLS.map(name => {
            return this.renderColumnHead(name, settings);
        }).filter(header => !!header);
        const colCount = headers.length + 1;

        let rows;

        if (!jobs || !jobs.length) {
            let key = `no-jobs-${title}`;
            rows = [
                <tr key={key}>
                    <td className="muted small" colSpan={colCount}>{translate.gettext("No jobs executed")}</td>
                </tr>
            ];
        } else {
            rows = jobs.filter((job: SU.IJob) => this.applyTypeAndStateFilter(job, settings)).map((job: SU.IJob, idx) => {
                job = this.assignLastLog(job);
                return this.renderRow(job, idx, settings);
            });
        }

        return (
            <div>
                <h5>{title}</h5>
                <table key={key} className="table">
                    <thead>
                        <tr>
                            {headers}
                        </tr>
                    </thead>
                    <tbody>
                        {rows}
                    </tbody>
                </table>
            </div>
        );
    }
}


// WEBPACK FOOTER //
// ./src/components/widget/su/listJobs/JobTable.tsx